// [목적] 운영 콘솔 4개 탭 패널 — 통계 · 수집 이력 · 프롬프트 · 신고 처리
// [이유] M9 AdminPage 최소판. 시안 docs/플리오랩/gangwonfolio-trip.jsx AdminScreen(대시보드/POI 동기/신고/프롬프트) 구성을
//         MUI 컴포넌트 + packages/ui 테마 토큰(palette·spacing·typography)으로 재구성.
// [사이드 이팩트] GET /admin/* 조회. 수집 실행(POST /admin/ingest-tasks/run) · 신고 상태 변경(PATCH /admin/reports/:id).
// [수정 시 고려사항] 색·간격은 theme 키(sx)만 사용 — hex/px 금지. 응답 필드명은 AdminRepository 매핑과 동기.

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useState, type ReactNode } from 'react';

import { api, useAsync, useCursorList, type ApiError, type Page } from './api';

const mono = { fontFamily: tokens.typography.fontFamily.mono };

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleString('ko-KR') : '—');

/**
 * [목적] 로딩·에러·빈 상태 공통 표시
 * [이유] 4개 탭 상태 UI 일관성 + 스크린리더 알림(role=status/alert)
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] children 은 데이터가 있을 때만 렌더
 */
function StateView(props: {
  loading: boolean;
  error?: ApiError;
  empty: boolean;
  emptyText: string;
  onRetry: () => void;
  children: ReactNode;
}) {
  const { loading, error, empty, emptyText, onRetry, children } = props;
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry} aria-label="다시 시도">
            다시 시도
          </Button>
        }
      >
        {error.message}
      </Alert>
    );
  }
  if (loading && empty) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{ display: 'flex', justifyContent: 'center', py: 10 }}
      >
        <CircularProgress aria-label="불러오는 중" />
      </Box>
    );
  }
  if (empty) {
    return (
      <Typography role="status" color="text.secondary" sx={{ py: 10, textAlign: 'center' }}>
        {emptyText}
      </Typography>
    );
  }
  return <>{children}</>;
}

function MoreButton(props: { show: boolean; loading: boolean; onClick: () => void }) {
  if (!props.show) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <Button
        variant="outlined"
        onClick={props.onClick}
        disabled={props.loading}
        aria-label="더 불러오기"
      >
        {props.loading ? '불러오는 중…' : '더 보기'}
      </Button>
    </Box>
  );
}

// ── 통계 ────────────────────────────────────────────────────────────────────

interface Stats {
  users: { total: number; last7d: number };
  folios: { total: number; last7d: number };
  reports: { open: number };
}

export function StatsPanel() {
  const { data, loading, error, reload } = useAsync(() => api<Stats>('/admin/stats'));
  const cards = data
    ? [
        { label: '전체 사용자', value: data.users.total, sub: `최근 7일 +${data.users.last7d}` },
        { label: '전체 폴리오', value: data.folios.total, sub: `최근 7일 +${data.folios.last7d}` },
        {
          label: '미처리 신고',
          value: data.reports.open,
          sub: '상태 open',
          danger: data.reports.open > 0,
        },
      ]
    : [];
  return (
    <StateView
      loading={loading}
      error={error}
      empty={!data}
      emptyText="통계가 없습니다."
      onRetry={reload}
    >
      <Box
        sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}
      >
        {cards.map((c) => (
          <Card key={c.label} variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {c.label}
              </Typography>
              <Typography
                variant="h3"
                sx={{ ...mono, color: c.danger ? 'error.main' : 'text.primary' }}
              >
                {c.value.toLocaleString('ko-KR')}
              </Typography>
              <Typography variant="body2" color="success.main">
                {c.sub}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </StateView>
  );
}

// ── 수집 이력 ───────────────────────────────────────────────────────────────

interface IngestTask {
  id: string;
  service: string;
  endpoint: string;
  runKey: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  isComplete: boolean;
  attempts: number;
  fetchedCount: number;
  totalCount: number | null;
  error: string | null;
  finishedAt: string | null;
}

const TASK_COLOR = {
  success: 'success',
  failed: 'error',
  running: 'warning',
  pending: 'default',
} as const;

export function IngestPanel() {
  const list = useCursorList((c) =>
    api<Page<IngestTask>>(
      `/admin/ingest-tasks?limit=20${c ? `&cursor=${encodeURIComponent(c)}` : ''}`,
    ),
  );
  const [run, setRun] = useState<{ busy: boolean; msg?: string; err?: string }>({ busy: false });

  const trigger = async () => {
    setRun({ busy: true });
    try {
      const r = await api<{ runKey: string }>('/admin/ingest-tasks/run', {
        method: 'POST',
        body: '{}',
      });
      setRun({ busy: false, msg: `수집 작업을 대기열에 추가했습니다 (${r.runKey}).` });
    } catch (e) {
      setRun({ busy: false, err: (e as ApiError).message });
    }
  };

  return (
    <Stack spacing={4}>
      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">TourAPI 수집</Typography>
            <Typography variant="body2" color="text.secondary">
              매일 00:00 KST 자동 실행. 수동 실행은 일일 호출 예산을 소모합니다.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={trigger}
            disabled={run.busy}
            aria-label="TourAPI 수집 지금 실행"
          >
            {run.busy ? '요청 중…' : '지금 수집 실행'}
          </Button>
        </CardContent>
      </Card>
      {run.msg && <Alert severity="success">{run.msg}</Alert>}
      {run.err && <Alert severity="error">{run.err}</Alert>}

      <StateView
        loading={list.loading}
        error={list.error}
        empty={list.items.length === 0}
        emptyText="수집 이력이 없습니다."
        onRetry={list.reload}
      >
        <TableContainer component={Card} variant="outlined">
          <Table size="small" aria-label="수집 작업 이력">
            <TableHead>
              <TableRow>
                <TableCell>실행 키</TableCell>
                <TableCell>엔드포인트</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="right">건수</TableCell>
                <TableCell align="right">시도</TableCell>
                <TableCell>에러</TableCell>
                <TableCell>종료</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.items.map((t) => (
                <TableRow key={t.id}>
                  <TableCell sx={mono}>{t.runKey}</TableCell>
                  <TableCell>
                    {t.service}
                    <Typography variant="body2" color="text.secondary" sx={mono}>
                      {t.endpoint}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={TASK_COLOR[t.status]} label={t.status} />
                  </TableCell>
                  <TableCell align="right" sx={mono}>
                    {t.fetchedCount}/{t.totalCount ?? '?'}
                  </TableCell>
                  <TableCell align="right" sx={mono}>
                    {t.attempts}
                  </TableCell>
                  <TableCell sx={{ ...mono, color: 'error.main', wordBreak: 'break-all' }}>
                    {t.error ?? ''}
                  </TableCell>
                  <TableCell>{fmtDate(t.finishedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <MoreButton show={list.hasMore} loading={list.loading} onClick={() => void list.more()} />
      </StateView>
    </Stack>
  );
}

// ── 프롬프트 ────────────────────────────────────────────────────────────────

interface Prompt {
  id: string;
  name: string;
  version: string;
  active: boolean;
  body: string;
  createdAt: string;
}

export function PromptsPanel() {
  const { data, loading, error, reload } = useAsync(() =>
    api<{ items: Prompt[] }>('/admin/prompts'),
  );
  const items = data?.items ?? [];
  return (
    <Stack spacing={4}>
      <Alert severity="info">
        현재 버전에서는 프롬프트를 DB 에서 읽는 서비스가 없어 조회만 제공합니다.
      </Alert>
      <StateView
        loading={loading}
        error={error}
        empty={items.length === 0}
        emptyText="등록된 프롬프트가 없습니다."
        onRetry={reload}
      >
        <Stack spacing={3}>
          {items.map((p) => (
            <Card key={p.id} variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  {p.active && <Chip size="small" color="success" label="ACTIVE" />}
                  <Typography sx={{ ...mono, fontWeight: 'bold', flex: 1 }}>{p.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={mono}>
                    v{p.version}
                  </Typography>
                </Stack>
                <Box
                  component="pre"
                  tabIndex={0}
                  aria-label={`${p.name} 프롬프트 본문`}
                  sx={{
                    ...mono,
                    m: 0,
                    p: 3,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    maxHeight: '40vh',
                    overflow: 'auto',
                    typography: 'body2',
                  }}
                >
                  {p.body}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </StateView>
    </Stack>
  );
}

// ── 신고 ────────────────────────────────────────────────────────────────────

type ReportStatus = 'open' | 'resolved' | 'rejected' | 'escalated';

interface Report {
  id: string;
  targetKind: string;
  targetId: string;
  reason: string;
  detail: string | null;
  status: ReportStatus;
  reporterHandle: string | null;
  createdAt: string;
}

const REPORT_LABEL: Record<ReportStatus, string> = {
  open: '오픈',
  escalated: '상향',
  resolved: '처리됨',
  rejected: '기각',
};
const TARGET_LABEL: Record<string, string> = {
  portfolio: '폴리오',
  comment: '댓글',
  user: '사용자',
  poi: 'POI',
  festival_comment: '축제 댓글',
};

export function ReportsPanel() {
  const [status, setStatus] = useState<ReportStatus | 'all'>('open');
  const list = useCursorList(
    (c) =>
      api<Page<Report>>(
        `/admin/reports?limit=20${status === 'all' ? '' : `&status=${status}`}${c ? `&cursor=${encodeURIComponent(c)}` : ''}`,
      ),
    status,
  );
  const [actionError, setActionError] = useState<string>();
  const [busyId, setBusyId] = useState<string>();

  const decide = async (id: string, next: 'resolved' | 'rejected') => {
    setBusyId(id);
    setActionError(undefined);
    try {
      await api(`/admin/reports/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      });
      await list.reload();
    } catch (e) {
      setActionError((e as ApiError).message);
    } finally {
      setBusyId(undefined);
    }
  };

  return (
    <Stack spacing={4}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={status}
        onChange={(_, v: ReportStatus | 'all' | null) => v && setStatus(v)}
        aria-label="신고 상태 필터"
      >
        {(['open', 'escalated', 'resolved', 'rejected', 'all'] as const).map((s) => (
          <ToggleButton key={s} value={s}>
            {s === 'all' ? '전체' : REPORT_LABEL[s]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {actionError && <Alert severity="error">{actionError}</Alert>}

      <StateView
        loading={list.loading}
        error={list.error}
        empty={list.items.length === 0}
        emptyText="해당 상태의 신고가 없습니다."
        onRetry={list.reload}
      >
        <Stack spacing={3}>
          {list.items.map((r) => {
            const pending = r.status === 'open' || r.status === 'escalated';
            return (
              <Card key={r.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Chip
                      size="small"
                      color={pending ? 'error' : 'success'}
                      label={REPORT_LABEL[r.status]}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {TARGET_LABEL[r.targetKind] ?? r.targetKind}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={mono}>
                      {r.reporterHandle ? `@${r.reporterHandle}` : '탈퇴 사용자'} ·{' '}
                      {fmtDate(r.createdAt)}
                    </Typography>
                  </Stack>
                  <Typography variant="h4">{r.reason}</Typography>
                  {r.detail && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {r.detail}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ ...mono, mt: 1 }}>
                    대상 ID {r.targetId}
                  </Typography>
                  {pending && (
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        color="error"
                        disabled={busyId === r.id}
                        onClick={() => void decide(r.id, 'resolved')}
                        aria-label={`신고 ${r.reason} 처리 완료`}
                      >
                        처리
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={busyId === r.id}
                        onClick={() => void decide(r.id, 'rejected')}
                        aria-label={`신고 ${r.reason} 기각`}
                      >
                        기각
                      </Button>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
        <MoreButton show={list.hasMore} loading={list.loading} onClick={() => void list.more()} />
      </StateView>
    </Stack>
  );
}
